import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumCreditType } from "./enums/enumCreditType.generated";
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumEireMovementType } from "./enums/enumEireMovementType.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumOrderStatus } from "./enums/enumOrderStatus.generated";
import { EnumSpiritDutyExemptionReason } from "./enums/enumSpiritDutyExemptionReason.generated";
import { EnumStandardOrder } from "./enums/enumStandardOrder.generated";

// Model Objects

export class CreditSummary extends ModelObject<CreditSummary> {

    public id: Guid = Guid.createEmpty();
    /**
    * Credit Number
    * Not Nullable
    */
    public creditNumber: string = "";
    /**
    * Outlet ID
    */
    public outletId: Guid = Guid.createEmpty();
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the credit was cancelled.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    /**
    * Effective Date of the Credit - Ie the accounting Date. This is also the date at which duty would be claimed, but not the date which would be the basis of the duty rate calculation (see OriginalDespatchDate).
    */
    public creditDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Date of Original Despatch. This is the date used as the basis for the duty rate calculation.
    */
    public originalDespatchDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Status : 0 = None, 1 = Open, 2 = Complete, 3 = Historic, 4 = Cancelled, 5 = Posting, 6 = Draft
    */
    public status: EnumOrderStatus = EnumOrderStatus.none;
    /**
    * Our Reference
    * Not Nullable
    * Max Length : 1000
    */
    public ourReference: string = "";
    private _orderHeaderId: Guid | null = null;
    /**
    * Order header id of which the credit is associated to.
    */
    public get orderHeaderId(): Guid | null { return this._orderHeaderId }
    public set orderHeaderId(value: Guid | null) {
      this._orderHeaderId = value === undefined ? null : value;
    }
    private _ullageId: Guid | null = null;
    /**
    * Optional Ullage ID to link to where the spoilt product came from.
    */
    public get ullageId(): Guid | null { return this._ullageId }
    public set ullageId(value: Guid | null) {
      this._ullageId = value === undefined ? null : value;
    }
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    /**
    * Reference name or number from the Accounts Link posting.
    * Not Nullable
    * Max Length : 1000
    */
    public accountsLinkReferenceName: string = "";
    /**
    * User who initially created the credit. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Timestamp at which the credit was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.creditNumber = jsonSerializedObject.creditNumber;
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.creditDate = LocalDate.parse(jsonSerializedObject.creditDate);
          this.originalDespatchDate = LocalDate.parse(jsonSerializedObject.originalDespatchDate);
          this.status = EnumOrderStatus.getByValue(jsonSerializedObject.status, this.status);
          this.ourReference = jsonSerializedObject.ourReference;
          this.orderHeaderId = jsonSerializedObject.orderHeaderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.orderHeaderId);
          this.ullageId = jsonSerializedObject.ullageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.ullageId);
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.accountsLinkReferenceName = jsonSerializedObject.accountsLinkReferenceName;
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.creditNumber = this.creditNumber;
        toRet.outletId = this.outletId.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.creditDate = this.creditDate.toString();
        toRet.originalDespatchDate = this.originalDespatchDate.toString();
        toRet.status = this.status.value;
        toRet.ourReference = this.ourReference;
        toRet.orderHeaderId = this.orderHeaderId == null ? null : this.orderHeaderId.toString();
        toRet.ullageId = this.ullageId == null ? null : this.ullageId.toString();
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.accountsLinkReferenceName = this.accountsLinkReferenceName;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "CreditSummary.Id cannot be empty"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "CreditSummary.OutletId cannot be empty"},
            {field: "ourReference", rule: (v: any) => v == null || v.length <= 1000 || "OurReference has too many characters (1000)"},
            {field: "orderHeaderId", rule: (v: any) => v == null || !v.isEmpty() || "CreditSummary.OrderHeaderId cannot be empty"},
            {field: "ullageId", rule: (v: any) => v == null || !v.isEmpty() || "CreditSummary.UllageId cannot be empty"},
            {field: "accountsLinkReferenceName", rule: (v: any) => v == null || v.length <= 1000 || "AccountsLinkReferenceName has too many characters (1000)"},
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "CreditSummary.CreatedByUserId cannot be empty"},
    ];
}


export class CreditSummariesResult extends ModelObject<CreditSummariesResult> {

    public matchingCount: number = 0;
    public credits: CreditSummary[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.credits = jsonSerializedObject.credits.map((v: any) => new CreditSummary(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.credits = this.credits.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "credits", rule: (v: any) => !!v || "credits is required"},
            {field: "credits", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as CreditSummary[]).reduce((acc: Array<string>, curr: CreditSummary) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class CreditFilter extends ModelObject<CreditFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _singleOutletId: Guid | null = null;
    public get singleOutletId(): Guid | null { return this._singleOutletId }
    public set singleOutletId(value: Guid | null) {
      this._singleOutletId = value === undefined ? null : value;
    }
    private _singleGroupId: Guid | null = null;
    public get singleGroupId(): Guid | null { return this._singleGroupId }
    public set singleGroupId(value: Guid | null) {
      this._singleGroupId = value === undefined ? null : value;
    }
    public singleCreditNumber: string = "";
    private _fromCreditDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) credit date
    */
    public get fromCreditDate(): LocalDate | null { return this._fromCreditDate }
    public set fromCreditDate(value: LocalDate | null) {
      this._fromCreditDate = value === undefined ? null : value;
    }
    private _toCreditDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) credit date
    */
    public get toCreditDate(): LocalDate | null { return this._toCreditDate }
    public set toCreditDate(value: LocalDate | null) {
      this._toCreditDate = value === undefined ? null : value;
    }
    private _limitStatuses: EnumOrderStatus[] | null = null;
    /**
    * If provided only matching statuses are returned
    */
    public get limitStatuses(): EnumOrderStatus[] | null { return this._limitStatuses }
    public set limitStatuses(value: EnumOrderStatus[] | null) {
      this._limitStatuses = value === undefined ? null : value;
    }
    private _limitCreditIds: Guid[] | null = null;
    public get limitCreditIds(): Guid[] | null { return this._limitCreditIds }
    public set limitCreditIds(value: Guid[] | null) {
      this._limitCreditIds = value === undefined ? null : value;
    }
    private _limitUllageIds: Guid[] | null = null;
    public get limitUllageIds(): Guid[] | null { return this._limitUllageIds }
    public set limitUllageIds(value: Guid[] | null) {
      this._limitUllageIds = value === undefined ? null : value;
    }
    private _standardOrderTypes: EnumStandardOrder[] | null = null;
    public get standardOrderTypes(): EnumStandardOrder[] | null { return this._standardOrderTypes }
    public set standardOrderTypes(value: EnumStandardOrder[] | null) {
      this._standardOrderTypes = value === undefined ? null : value;
    }
    private _fromCreatedAt: Instant | null = null;
    public get fromCreatedAt(): Instant | null { return this._fromCreatedAt }
    public set fromCreatedAt(value: Instant | null) {
      this._fromCreatedAt = value === undefined ? null : value;
    }
    private _toCreatedAt: Instant | null = null;
    public get toCreatedAt(): Instant | null { return this._toCreatedAt }
    public set toCreatedAt(value: Instant | null) {
      this._toCreatedAt = value === undefined ? null : value;
    }
    private _fromCancelledAt: Instant | null = null;
    public get fromCancelledAt(): Instant | null { return this._fromCancelledAt }
    public set fromCancelledAt(value: Instant | null) {
      this._fromCancelledAt = value === undefined ? null : value;
    }
    private _toCancelledAt: Instant | null = null;
    public get toCancelledAt(): Instant | null { return this._toCancelledAt }
    public set toCancelledAt(value: Instant | null) {
      this._toCancelledAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.singleOutletId = jsonSerializedObject.singleOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.singleOutletId);
          this.singleGroupId = jsonSerializedObject.singleGroupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.singleGroupId);
          this.singleCreditNumber = jsonSerializedObject.singleCreditNumber;
          this.fromCreditDate = jsonSerializedObject.fromCreditDate == null ? null : LocalDate.parse(jsonSerializedObject.fromCreditDate);
          this.toCreditDate = jsonSerializedObject.toCreditDate == null ? null : LocalDate.parse(jsonSerializedObject.toCreditDate);
          this.limitStatuses = jsonSerializedObject.limitStatuses == null ? null : jsonSerializedObject.limitStatuses.map((v: any) => EnumOrderStatus.getByValue(v));
          this.limitCreditIds = jsonSerializedObject.limitCreditIds == null ? null : jsonSerializedObject.limitCreditIds.map((v: any) => Guid.parseOrEmpty(v));
          this.limitUllageIds = jsonSerializedObject.limitUllageIds == null ? null : jsonSerializedObject.limitUllageIds.map((v: any) => Guid.parseOrEmpty(v));
          this.standardOrderTypes = jsonSerializedObject.standardOrderTypes == null ? null : jsonSerializedObject.standardOrderTypes.map((v: any) => EnumStandardOrder.getByValue(v));
          this.fromCreatedAt = jsonSerializedObject.fromCreatedAt == null ? null : Instant.parse(jsonSerializedObject.fromCreatedAt);
          this.toCreatedAt = jsonSerializedObject.toCreatedAt == null ? null : Instant.parse(jsonSerializedObject.toCreatedAt);
          this.fromCancelledAt = jsonSerializedObject.fromCancelledAt == null ? null : Instant.parse(jsonSerializedObject.fromCancelledAt);
          this.toCancelledAt = jsonSerializedObject.toCancelledAt == null ? null : Instant.parse(jsonSerializedObject.toCancelledAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.singleOutletId = this.singleOutletId == null ? null : this.singleOutletId.toString();
        toRet.singleGroupId = this.singleGroupId == null ? null : this.singleGroupId.toString();
        toRet.singleCreditNumber = this.singleCreditNumber;
        toRet.fromCreditDate = this.fromCreditDate == null ? null : this.fromCreditDate.toString();
        toRet.toCreditDate = this.toCreditDate == null ? null : this.toCreditDate.toString();
        toRet.limitStatuses = this.limitStatuses == null ? null : this.limitStatuses.map(v => v.value);
        toRet.limitCreditIds = this.limitCreditIds == null ? null : this.limitCreditIds.map(v => v.toString());
        toRet.limitUllageIds = this.limitUllageIds == null ? null : this.limitUllageIds.map(v => v.toString());
        toRet.standardOrderTypes = this.standardOrderTypes == null ? null : this.standardOrderTypes.map(v => v.value);
        toRet.fromCreatedAt = this.fromCreatedAt == null ? null : this.fromCreatedAt.toString();
        toRet.toCreatedAt = this.toCreatedAt == null ? null : this.toCreatedAt.toString();
        toRet.fromCancelledAt = this.fromCancelledAt == null ? null : this.fromCancelledAt.toString();
        toRet.toCancelledAt = this.toCancelledAt == null ? null : this.toCancelledAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
            {field: "singleOutletId", rule: (v: any) => v == null || !v.isEmpty() || "CreditFilter.SingleOutletId cannot be empty"},
            {field: "singleGroupId", rule: (v: any) => v == null || !v.isEmpty() || "CreditFilter.SingleGroupId cannot be empty"},
    ];
}


export class CreditHeaderReadOnly extends ModelObject<CreditHeaderReadOnly> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Reason Credit Cancelled
    * Not Nullable
    * Max Length : 100000
    */
    public cancellationReason: string = "";
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the credit was cancelled.
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    private _cancelledByUserId: Guid | null = null;
    /**
    * User who cancelled the credit.
    */
    public get cancelledByUserId(): Guid | null { return this._cancelledByUserId }
    public set cancelledByUserId(value: Guid | null) {
      this._cancelledByUserId = value === undefined ? null : value;
    }
    /**
    * Credit Number
    * Not Nullable
    */
    public creditNumber: string = "";
    /**
    * Credit Terms Description - Frozen at credit entry so historic invoices can print original details.
    * Not Nullable
    * Max Length : 1000
    */
    public creditTermsDescription: string = "";
    /**
    * Credit Type : 0 = None, 1 = CreditNoDutyReclaim, 2 = DutyOnlyReclaim, 3 = CreditWithDutyReclaim
    */
    public creditType: EnumCreditType = EnumCreditType.none;
    private _groupId: Guid | null = null;
    /**
    * Group (if any)
    */
    public get groupId(): Guid | null { return this._groupId }
    public set groupId(value: Guid | null) {
      this._groupId = value === undefined ? null : value;
    }
    /**
    * Is this a BeerflexOrder (DDS) Credit
    */
    public isBeerflexCredit: boolean = false;
    /**
    * Outlet ID
    */
    public outletId: Guid = Guid.createEmpty();
    /**
    * Outlet Name
    * Not Nullable
    * Max Length : 1000
    */
    public outletName: string = "";
    /**
    * Outlet Rating of Outlet at Time of credit. Stored for potential future reporting.
    */
    public outletRatingId: Guid = Guid.createEmpty();
    private _outletTypeId: Guid | null = null;
    /**
    * Outlet Type of Outlet at Time of credit. Stored for potential future reporting.
    */
    public get outletTypeId(): Guid | null { return this._outletTypeId }
    public set outletTypeId(value: Guid | null) {
      this._outletTypeId = value === undefined ? null : value;
    }
    private _salesAreaId: Guid | null = null;
    /**
    * The Sales Area which the outlet was in at the time of the credit. Stored for potential future reporting.
    */
    public get salesAreaId(): Guid | null { return this._salesAreaId }
    public set salesAreaId(value: Guid | null) {
      this._salesAreaId = value === undefined ? null : value;
    }
    private _salesCodeId: Guid | null = null;
    /**
    * The Sales Code which the outlet was in at the time of the credit. Stored for potential future reporting.
    */
    public get salesCodeId(): Guid | null { return this._salesCodeId }
    public set salesCodeId(value: Guid | null) {
      this._salesCodeId = value === undefined ? null : value;
    }
    /**
    * Status : 0 = None, 1 = Open, 2 = Complete, 3 = Historic, 4 = Cancelled, 5 = Posting, 6 = Draft
    */
    public status: EnumOrderStatus = EnumOrderStatus.none;
    /**
    * Total Cost Price of Credit. (Inc Duty as applicable, Ex VAT).
    */
    public totalCostNet: number = 0;
    /**
    * Total Net Price of Credit.
    */
    public totalNetPrice: number = 0;
    /**
    * Total VAT of Credit.
    */
    public totalVat: number = 0;
    /**
    * Timestamp at which the credit was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * Description of the current error which is preventing this credit from being posted to accounts. Ie the error (if any) which occurred  when this was last attempted to post. The presence of text in this field implies there is a known current error so posting will not be reattempted. To reattempt posting this field must be cleared.
    * Not Nullable
    * Max Length : 100000
    */
    public currentPostingError: string = "";
    /**
    * Set to true when stock is adjusted for this credit (occurs once posting to accounts succeeds). When true it implies that the items on the credit should not be modified.
    */
    public hasStockBeenAdjusted: boolean = false;
    /**
    * The approximate amount of duty which has/will be reclaimed in fulfilling this credit. This represents the amount of duty included in the total net price, regardless of whether that duty would be reclaimed directly due to this specific credit. For example, if the credit is to non-bonded then the duty is not being reclaimed but that duty element will be included in the duty cost of sale. It is approximate because actual duty return calculations will be rounded across multiple orders/credits so cannot be exactly attributed to each individual credit. 
    */
    public totalApproximateDutyCostOfSale: number = 0;
    /**
    * User who initially created the credit. Captured by system not user editable.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Total Regional Tax of Credit. Updated when lines changed. Not editable.
    */
    public totalRegionalTax: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.cancellationReason = jsonSerializedObject.cancellationReason;
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.cancelledByUserId = jsonSerializedObject.cancelledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.cancelledByUserId);
          this.creditNumber = jsonSerializedObject.creditNumber;
          this.creditTermsDescription = jsonSerializedObject.creditTermsDescription;
          this.creditType = EnumCreditType.getByValue(jsonSerializedObject.creditType, this.creditType);
          this.groupId = jsonSerializedObject.groupId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.groupId);
          this.isBeerflexCredit = jsonSerializedObject.isBeerflexCredit;
          this.outletId = Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.outletName = jsonSerializedObject.outletName;
          this.outletRatingId = Guid.parseOrEmpty(jsonSerializedObject.outletRatingId);
          this.outletTypeId = jsonSerializedObject.outletTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.salesAreaId = jsonSerializedObject.salesAreaId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesAreaId);
          this.salesCodeId = jsonSerializedObject.salesCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.salesCodeId);
          this.status = EnumOrderStatus.getByValue(jsonSerializedObject.status, this.status);
          this.totalCostNet = jsonSerializedObject.totalCostNet;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.totalVat = jsonSerializedObject.totalVat;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.currentPostingError = jsonSerializedObject.currentPostingError;
          this.hasStockBeenAdjusted = jsonSerializedObject.hasStockBeenAdjusted;
          this.totalApproximateDutyCostOfSale = jsonSerializedObject.totalApproximateDutyCostOfSale;
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.totalRegionalTax = jsonSerializedObject.totalRegionalTax;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.cancellationReason = this.cancellationReason;
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.cancelledByUserId = this.cancelledByUserId == null ? null : this.cancelledByUserId.toString();
        toRet.creditNumber = this.creditNumber;
        toRet.creditTermsDescription = this.creditTermsDescription;
        toRet.creditType = this.creditType.value;
        toRet.groupId = this.groupId == null ? null : this.groupId.toString();
        toRet.isBeerflexCredit = this.isBeerflexCredit;
        toRet.outletId = this.outletId.toString();
        toRet.outletName = this.outletName;
        toRet.outletRatingId = this.outletRatingId.toString();
        toRet.outletTypeId = this.outletTypeId == null ? null : this.outletTypeId.toString();
        toRet.salesAreaId = this.salesAreaId == null ? null : this.salesAreaId.toString();
        toRet.salesCodeId = this.salesCodeId == null ? null : this.salesCodeId.toString();
        toRet.status = this.status.value;
        toRet.totalCostNet = this.totalCostNet;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.totalVat = this.totalVat;
        toRet.createdAt = this.createdAt.toString();
        toRet.currentPostingError = this.currentPostingError;
        toRet.hasStockBeenAdjusted = this.hasStockBeenAdjusted;
        toRet.totalApproximateDutyCostOfSale = this.totalApproximateDutyCostOfSale;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.totalRegionalTax = this.totalRegionalTax;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CreditHeaderReadOnly.Id cannot be empty"},
            {field: "cancellationReason", rule: (v: any) => v == null || v.length <= 100000 || "CancellationReason has too many characters (100000)"},
            {field: "cancelledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeaderReadOnly.CancelledByUserId cannot be empty"},
            {field: "creditTermsDescription", rule: (v: any) => v == null || v.length <= 1000 || "CreditTermsDescription has too many characters (1000)"},
            {field: "groupId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeaderReadOnly.GroupId cannot be empty"},
            {field: "outletId", rule: (v: any) => !v.isEmpty() || "CreditHeaderReadOnly.OutletId cannot be empty"},
            {field: "outletName", rule: (v: any) => v == null || v.length <= 1000 || "OutletName has too many characters (1000)"},
            {field: "outletRatingId", rule: (v: any) => !v.isEmpty() || "CreditHeaderReadOnly.OutletRatingId cannot be empty"},
            {field: "outletTypeId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeaderReadOnly.OutletTypeId cannot be empty"},
            {field: "salesAreaId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeaderReadOnly.SalesAreaId cannot be empty"},
            {field: "salesCodeId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeaderReadOnly.SalesCodeId cannot be empty"},
            {field: "currentPostingError", rule: (v: any) => v == null || v.length <= 100000 || "CurrentPostingError has too many characters (100000)"},
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "CreditHeaderReadOnly.CreatedByUserId cannot be empty"},
    ];
}


export class CreditHeader extends BaseModelObject<CreditHeader> {

    /**
    * Effective Date of the Credit - Ie the accounting Date. This is also the date at which duty would be claimed, but not the date which would be the basis of the duty rate calculation (see OriginalDespatchDate).
    */
    public creditDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Credit Address Line 1
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddress1: string = "";
    /**
    * Credit Address Line 2
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddress2: string = "";
    /**
    * Credit Address Line 3
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddress3: string = "";
    /**
    * Credit Address Line 4
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddress4: string = "";
    /**
    * Credit Address Line 5
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddress5: string = "";
    /**
    * Credit Address Line 6
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddress6: string = "";
    /**
    * Credit Business Name
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddressBusiness: string = "";
    /**
    * Credit Recipient Name
    * Not Nullable
    * Max Length : 1000
    */
    public creditAddressRecipient: string = "";
    /**
    * Credit Post Code
    * Not Nullable
    * Max Length : 1000
    */
    public creditPostcode: string = "";
    /**
    * Credit Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Date of Original Despatch. This is the date used as the basis for the duty rate calculation.
    */
    public originalDespatchDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Our Reference
    * Not Nullable
    * Max Length : 1000
    */
    public ourReference: string = "";
    /**
    * Price List used for the credit. Stored for potential future reporting.
    */
    public priceListId: Guid = Guid.createEmpty();
    /**
    * Order Type 0;Full Price  - Duty Paid;1;Price Excluding Duty - Duty Suspended;2;Full Price - Duty Paid Elsewhere;3;Export
    */
    public standardOrderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;
    /**
    * VAT Code
    */
    public vatCodeId: Guid = Guid.createEmpty();
    public readOnly: CreditHeaderReadOnly = new CreditHeaderReadOnly();
    /**
    * The email address credit notes will be sent to. Copied from Outlet by default.
    * Not Nullable
    * Max Length : 1000
    */
    public emailAddress: string = "";
    /**
    * Email Recipient Name. Copied from Outlet / Group by default.
    * Not Nullable
    * Max Length : 1000
    */
    public emailRecipient: string = "";
    private _orderHeaderId: Guid | null = null;
    /**
    * Order header id of which the credit is associated to.
    */
    public get orderHeaderId(): Guid | null { return this._orderHeaderId }
    public set orderHeaderId(value: Guid | null) {
      this._orderHeaderId = value === undefined ? null : value;
    }
    private _ullageId: Guid | null = null;
    /**
    * Optional Ullage ID to link to where the spoilt product came from.
    */
    public get ullageId(): Guid | null { return this._ullageId }
    public set ullageId(value: Guid | null) {
      this._ullageId = value === undefined ? null : value;
    }
    /**
    * Whether an credit note has been sent for this credit.
    */
    public creditNoteSent: boolean = false;
    public germanBeerDutyMovementType: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;
    /**
    * Reference name or number from the Accounts Link posting.
    * Not Nullable
    * Max Length : 1000
    */
    public accountsLinkReferenceName: string = "";
    public eireDutyMovementType: EnumEireMovementType = EnumEireMovementType.unknown;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.creditDate = LocalDate.parse(jsonSerializedObject.creditDate);
          this.creditAddress1 = jsonSerializedObject.creditAddress1;
          this.creditAddress2 = jsonSerializedObject.creditAddress2;
          this.creditAddress3 = jsonSerializedObject.creditAddress3;
          this.creditAddress4 = jsonSerializedObject.creditAddress4;
          this.creditAddress5 = jsonSerializedObject.creditAddress5;
          this.creditAddress6 = jsonSerializedObject.creditAddress6;
          this.creditAddressBusiness = jsonSerializedObject.creditAddressBusiness;
          this.creditAddressRecipient = jsonSerializedObject.creditAddressRecipient;
          this.creditPostcode = jsonSerializedObject.creditPostcode;
          this.notes = jsonSerializedObject.notes;
          this.originalDespatchDate = LocalDate.parse(jsonSerializedObject.originalDespatchDate);
          this.ourReference = jsonSerializedObject.ourReference;
          this.priceListId = Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.standardOrderType = EnumStandardOrder.getByValue(jsonSerializedObject.standardOrderType, this.standardOrderType);
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.readOnly = new CreditHeaderReadOnly(jsonSerializedObject.readOnly);
          this.emailAddress = jsonSerializedObject.emailAddress;
          this.emailRecipient = jsonSerializedObject.emailRecipient;
          this.orderHeaderId = jsonSerializedObject.orderHeaderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.orderHeaderId);
          this.ullageId = jsonSerializedObject.ullageId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.ullageId);
          this.creditNoteSent = jsonSerializedObject.creditNoteSent;
          this.germanBeerDutyMovementType = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementType, this.germanBeerDutyMovementType);
          this.accountsLinkReferenceName = jsonSerializedObject.accountsLinkReferenceName;
          this.eireDutyMovementType = EnumEireMovementType.getByValue(jsonSerializedObject.eireDutyMovementType, this.eireDutyMovementType);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.creditDate = this.creditDate.toString();
        toRet.creditAddress1 = this.creditAddress1;
        toRet.creditAddress2 = this.creditAddress2;
        toRet.creditAddress3 = this.creditAddress3;
        toRet.creditAddress4 = this.creditAddress4;
        toRet.creditAddress5 = this.creditAddress5;
        toRet.creditAddress6 = this.creditAddress6;
        toRet.creditAddressBusiness = this.creditAddressBusiness;
        toRet.creditAddressRecipient = this.creditAddressRecipient;
        toRet.creditPostcode = this.creditPostcode;
        toRet.notes = this.notes;
        toRet.originalDespatchDate = this.originalDespatchDate.toString();
        toRet.ourReference = this.ourReference;
        toRet.priceListId = this.priceListId.toString();
        toRet.standardOrderType = this.standardOrderType.value;
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.emailAddress = this.emailAddress;
        toRet.emailRecipient = this.emailRecipient;
        toRet.orderHeaderId = this.orderHeaderId == null ? null : this.orderHeaderId.toString();
        toRet.ullageId = this.ullageId == null ? null : this.ullageId.toString();
        toRet.creditNoteSent = this.creditNoteSent;
        toRet.germanBeerDutyMovementType = this.germanBeerDutyMovementType.value;
        toRet.accountsLinkReferenceName = this.accountsLinkReferenceName;
        toRet.eireDutyMovementType = this.eireDutyMovementType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "creditAddress1", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddress1 has too many characters (1000)"},
            {field: "creditAddress2", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddress2 has too many characters (1000)"},
            {field: "creditAddress3", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddress3 has too many characters (1000)"},
            {field: "creditAddress4", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddress4 has too many characters (1000)"},
            {field: "creditAddress5", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddress5 has too many characters (1000)"},
            {field: "creditAddress6", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddress6 has too many characters (1000)"},
            {field: "creditAddressBusiness", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddressBusiness has too many characters (1000)"},
            {field: "creditAddressRecipient", rule: (v: any) => v == null || v.length <= 1000 || "CreditAddressRecipient has too many characters (1000)"},
            {field: "creditPostcode", rule: (v: any) => v == null || v.length <= 1000 || "CreditPostcode has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "ourReference", rule: (v: any) => v == null || v.length <= 1000 || "OurReference has too many characters (1000)"},
            {field: "priceListId", rule: (v: any) => !v.isEmpty() || "CreditHeader.PriceListId cannot be empty"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "CreditHeader.VatCodeId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "emailAddress", rule: (v: any) => v == null || v.length <= 1000 || "EmailAddress has too many characters (1000)"},
            {field: "emailRecipient", rule: (v: any) => v == null || v.length <= 1000 || "EmailRecipient has too many characters (1000)"},
            {field: "orderHeaderId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeader.OrderHeaderId cannot be empty"},
            {field: "ullageId", rule: (v: any) => v == null || !v.isEmpty() || "CreditHeader.UllageId cannot be empty"},
            {field: "accountsLinkReferenceName", rule: (v: any) => v == null || v.length <= 1000 || "AccountsLinkReferenceName has too many characters (1000)"},
    ];
}


export class CreditLineReadOnly extends ModelObject<CreditLineReadOnly> {

    public id: Guid = Guid.createEmpty();
    /**
    * Product ABV
    * Not Negative
    */
    public abv: number = 0;
    /**
    * Alcohol Type
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * Credit ID
    */
    public creditId: Guid = Guid.createEmpty();
    /**
    * General Ledger Code of Product at time of credit. Stored for potential future reporting.
    */
    public glCodeId: Guid = Guid.createEmpty();
    /**
    * Line Cost Price (Inc Duty, Exc Vat)
    * Not Negative
    */
    public lineCostNetPrice: number = 0;
    /**
    * Net (no VAT) Price refunded for the line.
    * Not Negative
    */
    public lineNetPrice: number = 0;
    /**
    * Total VAT
    * Not Negative
    */
    public lineVat: number = 0;
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer of the product - Required for lines with Alcohol.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    private _productBrandId: Guid | null = null;
    /**
    * Product Brand at time of Credit. Stored for potential future reporting.
    */
    public get productBrandId(): Guid | null { return this._productBrandId }
    public set productBrandId(value: Guid | null) {
      this._productBrandId = value === undefined ? null : value;
    }
    /**
    * Product Code
    * Not Nullable
    * Max Length : 1000
    */
    public productCode: string = "";
    /**
    * Stock Item Id
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Product Cost Price
    */
    public unitCostNetPrice: number = 0;
    /**
    * Product Dutyable Litres
    * Not Negative
    */
    public unitDutiableLitres: number = 0;
    /**
    * Product Litres
    * Not Negative
    */
    public unitLitres: number = 0;
    /**
    * Product VAT percentage
    * Not Negative
    */
    public vatPercentage: number = 0;
    /**
    * Vat Code Applicable to the line. May come from Product or Order Header.
    */
    public vatCodeId: Guid = Guid.createEmpty();
    /**
    * Quantity for Duty Reclaim
    * Not Negative
    */
    public quantityForDutyReclaim: number = 0;
    private _pricingCategoryId: Guid | null = null;
    /**
    * Pricing Category.
    */
    public get pricingCategoryId(): Guid | null { return this._pricingCategoryId }
    public set pricingCategoryId(value: Guid | null) {
      this._pricingCategoryId = value === undefined ? null : value;
    }
    /**
    * The approximate amount of duty which has/will be reclaimed in fulfilling this credit. This represents the amount of duty included in the total net price, regardless of whether that duty would be reclaimed directly due to this specific credit. For example, if the credit is to non-bonded then the duty is not being reclaimed but that duty element will be included in the duty cost of sale. It is approximate because actual duty return calculations will be rounded across multiple orders/credits so cannot be exactly attributed to each individual credit. 
    */
    public approximateDutyCostOfSale: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.abv = jsonSerializedObject.abv;
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.creditId = Guid.parseOrEmpty(jsonSerializedObject.creditId);
          this.glCodeId = Guid.parseOrEmpty(jsonSerializedObject.glCodeId);
          this.lineCostNetPrice = jsonSerializedObject.lineCostNetPrice;
          this.lineNetPrice = jsonSerializedObject.lineNetPrice;
          this.lineVat = jsonSerializedObject.lineVat;
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.productBrandId = jsonSerializedObject.productBrandId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.productBrandId);
          this.productCode = jsonSerializedObject.productCode;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.unitCostNetPrice = jsonSerializedObject.unitCostNetPrice;
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.unitLitres = jsonSerializedObject.unitLitres;
          this.vatPercentage = jsonSerializedObject.vatPercentage;
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.quantityForDutyReclaim = jsonSerializedObject.quantityForDutyReclaim;
          this.pricingCategoryId = jsonSerializedObject.pricingCategoryId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.pricingCategoryId);
          this.approximateDutyCostOfSale = jsonSerializedObject.approximateDutyCostOfSale;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.abv = this.abv;
        toRet.alcoholType = this.alcoholType.value;
        toRet.creditId = this.creditId.toString();
        toRet.glCodeId = this.glCodeId.toString();
        toRet.lineCostNetPrice = this.lineCostNetPrice;
        toRet.lineNetPrice = this.lineNetPrice;
        toRet.lineVat = this.lineVat;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.productBrandId = this.productBrandId == null ? null : this.productBrandId.toString();
        toRet.productCode = this.productCode;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.unitCostNetPrice = this.unitCostNetPrice;
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.unitLitres = this.unitLitres;
        toRet.vatPercentage = this.vatPercentage;
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.quantityForDutyReclaim = this.quantityForDutyReclaim;
        toRet.pricingCategoryId = this.pricingCategoryId == null ? null : this.pricingCategoryId.toString();
        toRet.approximateDutyCostOfSale = this.approximateDutyCostOfSale;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "CreditLineReadOnly.Id cannot be empty"},
            {field: "creditId", rule: (v: any) => !v.isEmpty() || "CreditLineReadOnly.CreditId cannot be empty"},
            {field: "glCodeId", rule: (v: any) => !v.isEmpty() || "CreditLineReadOnly.GlCodeId cannot be empty"},
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "CreditLineReadOnly.ManufacturerId cannot be empty"},
            {field: "productBrandId", rule: (v: any) => v == null || !v.isEmpty() || "CreditLineReadOnly.ProductBrandId cannot be empty"},
            {field: "productCode", rule: (v: any) => v == null || v.length <= 1000 || "ProductCode has too many characters (1000)"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "CreditLineReadOnly.StockItemId cannot be empty"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "CreditLineReadOnly.VatCodeId cannot be empty"},
            {field: "pricingCategoryId", rule: (v: any) => v == null || !v.isEmpty() || "CreditLineReadOnly.PricingCategoryId cannot be empty"},
    ];
}


export class CreditLine extends ModelObject<CreditLine> {

    /**
    * This is Spoilt Beer - Duty reclaimable
    */
    public isSpoilt: boolean = false;
    /**
    * Quantity Credited
    * Not Negative
    */
    public quantity: number = 0;
    /**
    * Quantity to Re-Stock
    * Not Negative
    */
    public quantityToRestock: number = 0;
    private _stockLocationId: Guid | null = null;
    /**
    * Stock Location Id - For Restocking.
    */
    public get stockLocationId(): Guid | null { return this._stockLocationId }
    public set stockLocationId(value: Guid | null) {
      this._stockLocationId = value === undefined ? null : value;
    }
    /**
    * Product Unit List Price
    * Not Negative
    */
    public unitListNetPrice: number = 0;
    /**
    * Unit Discount Price
    * Not Negative
    */
    public unitNetPrice: number = 0;
    /**
    * Unit of Measure Id
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public readOnly: CreditLineReadOnly = new CreditLineReadOnly();
    public version: number = 0;
    private _plato: number | null = null;
    /**
    * Plato Value
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * Set true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment. This is a service offered by breweries in some countries, such as Australia, and can affect duty.
    */
    public isBrewedForPersonalUse: boolean = false;
    /**
    * Set true if this item is designed to be connected to a pump or pressurised gas system, such as those used in pubs. Some duty systems have different duty rates for beer packaged in these manners for selling to pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant. For example, a case of 12 x 500ml bottles would have a final unit litres of 0.5. Some duty systems have different rates depending upon this value.
    * Greater Than : 0
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    /**
    * Product Name
    * Not Nullable
    * Max Length : 1000
    */
    public productName: string = "";
    /**
    * True if the credit line was entered by volume, and should be shown as by volume when edited.
    */
    public showAsByVolume: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isSpoilt = jsonSerializedObject.isSpoilt;
          this.quantity = jsonSerializedObject.quantity;
          this.quantityToRestock = jsonSerializedObject.quantityToRestock;
          this.stockLocationId = jsonSerializedObject.stockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.unitListNetPrice = jsonSerializedObject.unitListNetPrice;
          this.unitNetPrice = jsonSerializedObject.unitNetPrice;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.readOnly = new CreditLineReadOnly(jsonSerializedObject.readOnly);
          this.version = jsonSerializedObject.version;
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.productName = jsonSerializedObject.productName;
          this.showAsByVolume = jsonSerializedObject.showAsByVolume;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isSpoilt = this.isSpoilt;
        toRet.quantity = this.quantity;
        toRet.quantityToRestock = this.quantityToRestock;
        toRet.stockLocationId = this.stockLocationId == null ? null : this.stockLocationId.toString();
        toRet.unitListNetPrice = this.unitListNetPrice;
        toRet.unitNetPrice = this.unitNetPrice;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.version = this.version;
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.productName = this.productName;
        toRet.showAsByVolume = this.showAsByVolume;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "CreditLine.StockLocationId cannot be empty"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "CreditLine.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "finalUnitLitres", rule: (v: any) => v == null || v > 0 || "FinalUnitLitres is not greater than minimum allowed value: 0"},
            {field: "productName", rule: (v: any) => v == null || v.length <= 1000 || "ProductName has too many characters (1000)"},
    ];
}


export class CreditRegionalTaxLine extends ModelObject<CreditRegionalTaxLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Credit Header Id
    */
    public creditId: Guid = Guid.createEmpty();
    /**
    * Regional Tax Id.
    */
    public regionalTaxId: Guid = Guid.createEmpty();
    /**
    * Tax rate for this regional tax at the time of evaluation. Not user editable.
    */
    public rate: number = 0;
    /**
    * Order total beer litres used to calculate the tax amount. Not user editable.
    */
    public litres: number = 0;
    /**
    * Calculated tax amount for this order and tax rate. Not user editable.
    */
    public taxAmount: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.creditId = Guid.parseOrEmpty(jsonSerializedObject.creditId);
          this.regionalTaxId = Guid.parseOrEmpty(jsonSerializedObject.regionalTaxId);
          this.rate = jsonSerializedObject.rate;
          this.litres = jsonSerializedObject.litres;
          this.taxAmount = jsonSerializedObject.taxAmount;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.creditId = this.creditId.toString();
        toRet.regionalTaxId = this.regionalTaxId.toString();
        toRet.rate = this.rate;
        toRet.litres = this.litres;
        toRet.taxAmount = this.taxAmount;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "CreditRegionalTaxLine.Id cannot be empty"},
            {field: "creditId", rule: (v: any) => !v.isEmpty() || "CreditRegionalTaxLine.CreditId cannot be empty"},
            {field: "regionalTaxId", rule: (v: any) => !v.isEmpty() || "CreditRegionalTaxLine.RegionalTaxId cannot be empty"},
    ];
}


export class Credit extends BaseModelObject<Credit> {

    public header: CreditHeader = new CreditHeader();
    public lines: CreditLine[] = [];
    public regionalTaxLines: CreditRegionalTaxLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new CreditHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new CreditLine(v));
          this.regionalTaxLines = jsonSerializedObject.regionalTaxLines.map((v: any) => new CreditRegionalTaxLine(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        toRet.regionalTaxLines = this.regionalTaxLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as CreditLine[]).reduce((acc: Array<string>, curr: CreditLine) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "regionalTaxLines", rule: (v: any) => !!v || "regionalTaxLines is required"},
            {field: "regionalTaxLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as CreditRegionalTaxLine[]).reduce((acc: Array<string>, curr: CreditRegionalTaxLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class FilteredCreditsResult extends ModelObject<FilteredCreditsResult> {

    public matchingCount: number = 0;
    public credits: Credit[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.credits = jsonSerializedObject.credits.map((v: any) => new Credit(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.credits = this.credits.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "credits", rule: (v: any) => !!v || "credits is required"},
            {field: "credits", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Credit[]).reduce((acc: Array<string>, curr: Credit) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class DutySuspendedCreditsResultSet extends ModelObject<DutySuspendedCreditsResultSet> {

    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public dutySuspendedCreditedLitres: number = 0;
    public dutySuspendedCreditedLitresOfAlcohol: number = 0;
    public orderType: EnumStandardOrder = EnumStandardOrder.fullPriceDutyPaid;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.dutySuspendedCreditedLitres = jsonSerializedObject.dutySuspendedCreditedLitres;
          this.dutySuspendedCreditedLitresOfAlcohol = jsonSerializedObject.dutySuspendedCreditedLitresOfAlcohol;
          this.orderType = EnumStandardOrder.getByValue(jsonSerializedObject.orderType, this.orderType);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.alcoholType = this.alcoholType.value;
        toRet.dutySuspendedCreditedLitres = this.dutySuspendedCreditedLitres;
        toRet.dutySuspendedCreditedLitresOfAlcohol = this.dutySuspendedCreditedLitresOfAlcohol;
        toRet.orderType = this.orderType.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class PartialCredit extends ModelObject<PartialCredit> {

    public header: CreditHeader = new CreditHeader();
    public lines: CreditLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new CreditHeader(jsonSerializedObject.header);
          this.lines = jsonSerializedObject.lines.map((v: any) => new CreditLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lines = this.lines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lines", rule: (v: any) => !!v || "lines is required"},
            {field: "lines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as CreditLine[]).reduce((acc: Array<string>, curr: CreditLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class CreditService extends ServiceBase {

  public async getCreditSummaries(tenantId: Guid, filter: CreditFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CreditSummariesResult> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new CreditSummariesResult(response.results);;
    return this.makeJsonRequest("credit/v1/getCreditSummaries", req, false, extractor, opts);
  };

  public async getCredits(tenantId: Guid, creditIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit[]> {
    const req = {
    tenantId:tenantId.toString(),
    creditIds:creditIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.credits.map((v: any) => new Credit(v));;
    return this.makeJsonRequest("credit/v1/getCredits", req, false, extractor, opts);
  };

  public async getCreditsByFilter(tenantId: Guid, filter: CreditFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredCreditsResult> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredCreditsResult(response.results);;
    return this.makeJsonRequest("credit/v1/getCreditsByFilter", req, false, extractor, opts);
  };

  public async createCredit(tenantId: Guid, outletId: Guid, standardOrderType: EnumStandardOrder, originalDespatchDate: LocalDate, priceListId: Guid, creditType: EnumCreditType, orderHeaderId: Guid | null, ullageId: Guid | null, germanBeerDutyMovementType: EnumGermanBeerDutyMovementType, eireDutyMovementType: EnumEireMovementType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    outletId:outletId.toString(),
    standardOrderType:standardOrderType.value,
    originalDespatchDate:originalDespatchDate.toString(),
    priceListId:priceListId.toString(),
    creditType:creditType.value,
    orderHeaderId:orderHeaderId == null ? null : orderHeaderId.toString(),
    ullageId:ullageId == null ? null : ullageId.toString(),
    germanBeerDutyMovementType:germanBeerDutyMovementType.value,
    eireDutyMovementType:eireDutyMovementType.value,
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/createCredit", req, true, extractor, opts);
  };

  public async buildCreditHeader(tenantId: Guid, outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CreditHeader> {
    const req = {
    tenantId:tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => new CreditHeader(response.header);;
    return this.makeJsonRequest("credit/v1/buildCreditHeader", req, false, extractor, opts);
  };

  public async updateCredit(tenantId: Guid, creditId: Guid, creditHeader: CreditHeader, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    creditHeader:creditHeader.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/updateCredit", req, true, extractor, opts);
  };

  public async addLineToCredit(tenantId: Guid, creditId: Guid, creditLineId: Guid, stockItemId: Guid, creditLine: CreditLine, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    creditLineId:creditLineId.toString(),
    stockItemId:stockItemId.toString(),
    creditLine:creditLine.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/addLineToCredit", req, true, extractor, opts);
  };

  public async updateCreditLine(tenantId: Guid, creditId: Guid, creditLineId: Guid, creditLine: CreditLine, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    creditLineId:creditLineId.toString(),
    creditLine:creditLine.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/updateCreditLine", req, true, extractor, opts);
  };

  public async deleteLinesFromCredit(tenantId: Guid, creditId: Guid, creditLineIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    creditLineIds:creditLineIds.map(v => v.toString()),
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/deleteLinesFromCredit", req, true, extractor, opts);
  };

  public async cancelCredit(tenantId: Guid, creditId: Guid, cancellationReason: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    cancellationReason:cancellationReason,
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/cancelCredit", req, true, extractor, opts);
  };

  public async completeCredit(tenantId: Guid, creditId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/completeCredit", req, true, extractor, opts);
  };

  public async uncompleteCredit(tenantId: Guid, creditId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Credit> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    };
    const extractor = (response:any) => new Credit(response.credit);;
    return this.makeJsonRequest("credit/v1/uncompleteCredit", req, true, extractor, opts);
  };

  public async archiveCredits(tenantId: Guid, creditIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditIds:creditIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/archiveCredits", req, true, extractor, opts);
  };

  public async evaluateDutySuspendedCredits(tenantId: Guid, fromDutyDateInclusive: LocalDate, toDutyDateInclusive: LocalDate, limitToGivenAlcoholTypes: boolean, limitedAlcoholTypes: readonly EnumAlcoholType[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DutySuspendedCreditsResultSet[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDutyDateInclusive:fromDutyDateInclusive.toString(),
    toDutyDateInclusive:toDutyDateInclusive.toString(),
    limitToGivenAlcoholTypes:limitToGivenAlcoholTypes,
    limitedAlcoholTypes:limitedAlcoholTypes.map(v => v.value),
    };
    const extractor = (response:any) => response.results.map((v: any) => new DutySuspendedCreditsResultSet(v));;
    return this.makeJsonRequest("credit/v1/evaluateDutySuspendedCredits", req, false, extractor, opts);
  };

  public async generateCreditNoteDocuments(tenantId: Guid, creditNoteIds: readonly Guid[], documentFormat: EnumDocumentFileType, documentTemplateIdOverride: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    creditNoteIds:creditNoteIds.map(v => v.toString()),
    documentFormat:documentFormat.value,
    documentTemplateIdOverride:documentTemplateIdOverride == null ? null : documentTemplateIdOverride.toString(),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("credit/v1/generateCreditNoteDocuments", req, false, extractor, opts);
  };

  public async markCreditsForPosting(tenantId: Guid, creditNoteIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditNoteIds:creditNoteIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/markCreditsForPosting", req, true, extractor, opts);
  };

  public async unmarkCreditsForPosting(tenantId: Guid, creditNoteIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditNoteIds:creditNoteIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/unmarkCreditsForPosting", req, true, extractor, opts);
  };

  public async recordCreditPostingResult(tenantId: Guid, creditNoteId: Guid, postingError: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditNoteId:creditNoteId.toString(),
    postingError:postingError,
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/recordCreditPostingResult", req, true, extractor, opts);
  };

  public async emailCreditNote(tenantId: Guid, creditNoteId: Guid, documentFormat: EnumDocumentFileType, specifiedEmailAddresses: readonly string[] | null, emailSubject: string | null, emailBody: string | null, emailAddressesToCc: readonly string[] | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditNoteId:creditNoteId.toString(),
    documentFormat:documentFormat.value,
    specifiedEmailAddresses:specifiedEmailAddresses == null ? null : specifiedEmailAddresses.map(v => v),
    emailSubject:emailSubject == null ? null : emailSubject,
    emailBody:emailBody == null ? null : emailBody,
    emailAddressesToCc:emailAddressesToCc == null ? null : emailAddressesToCc.map(v => v),
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/emailCreditNote", req, false, extractor, opts);
  };

  public async updateCreditNotes(tenantId: Guid, creditId: Guid, notes: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    notes:notes,
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/updateCreditNotes", req, true, extractor, opts);
  };

  public async updateCreditDetailsToCurrent(tenantId: Guid, creditId: Guid, applyListPrices: boolean, applyGlCodes: boolean, applyVatCodes: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    creditId:creditId.toString(),
    applyListPrices:applyListPrices,
    applyGlCodes:applyGlCodes,
    applyVatCodes:applyVatCodes,
    };
    const extractor = null;
    return this.makeJsonRequest("credit/v1/updateCreditDetailsToCurrent", req, true, extractor, opts);
  };
}

export const creditService = new CreditService();